/**
 * 주렁주렁 여권 판매 부스 - API 클라이언트
 * (entrance-tablet에서 여권/회원 API만 추출)
 */

import axios from 'axios';
import { getAccessToken, setAccessToken } from '../contexts/AuthContext';

// API 기본 URL (환경변수에서 가져오거나 기본값 사용)
// export 하는 이유: 여권테스트 부여 탭(`PassportTestPage`)이 **아래 공용 `api` 를 쓸 수 없어**
// 자체 axios 인스턴스를 만든다(요청 인터셉터가 Authorization 을 STAFF JWT 로 덮어쓰기 때문).
// 그 인스턴스가 이 값을 **복제하지 않고 재사용**해야 컨테이너 빌드의
// `REACT_APP_API_URL=/api/v1` + nginx 프록시 짝이 두 곳에서 갈라지지 않는다.
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://192.168.0.113:59000/api/v1';

// ─── 전역 펜딩 카운터 (UI 로딩 오버레이용) ────────────────────────────
//  - 모든 axios 요청 시작 시 +1, 응답/에러 시 -1
//  - subscribePending(cb) 으로 React 측에서 구독
let __pendingCount = 0;
const __pendingListeners = new Set();
const __notifyPending = () => {
  for (const cb of __pendingListeners) {
    try { cb(__pendingCount); } catch { /* noop */ }
  }
};
export const subscribePending = (cb) => {
  __pendingListeners.add(cb);
  cb(__pendingCount);
  return () => __pendingListeners.delete(cb);
};
export const getPendingCount = () => __pendingCount;
const __incPending = () => { __pendingCount += 1; __notifyPending(); };
const __decPending = () => { __pendingCount = Math.max(0, __pendingCount - 1); __notifyPending(); };

// Axios 인스턴스 생성
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 요청 인터셉터 (로깅, 토큰 추가 등)
api.interceptors.request.use(
  (config) => {
    // JWT Access Token 추가
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // 디바이스 토큰이 있으면 헤더에 추가
    const deviceToken = localStorage.getItem('device_token');
    if (deviceToken) {
      config.headers['X-Device-Token'] = deviceToken;
    }

    // 매장 ID가 있으면 헤더에 추가
    const storeId = localStorage.getItem('store_id');
    if (storeId) {
      config.headers['X-Store-Id'] = storeId;
    }

    console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
    __incPending();
    return config;
  },
  (error) => {
    __decPending();
    return Promise.reject(error);
  }
);

// 응답 인터셉터 (에러 처리 + 401 자동 갱신)
api.interceptors.response.use(
  (response) => {
    __decPending();
    return response.data;
  },
  async (error) => {
    __decPending();
    const originalRequest = error.config;

    // 401 에러 시 Refresh Token으로 자동 갱신 시도
    // 인증 플로우 요청(login/logout/refresh)은 자동 갱신 대상에서 제외
    const authFlowPaths = ['/auth/login', '/auth/logout', '/auth/refresh'];
    const isAuthFlowRequest = authFlowPaths.some(p => originalRequest.url?.includes(p));
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthFlowRequest) {
      originalRequest._retry = true;

      try {
        const refreshResponse = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true }
        );
        const newToken = refreshResponse.data?.data?.access_token;
        if (newToken) {
          setAccessToken(newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh도 실패 → 로그인 페이지로 리다이렉트
        setAccessToken(null);
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    const errorResponse = {
      success: false,
      message: '서버와 통신 중 오류가 발생했습니다',
      error_code: 'NETWORK_ERROR',
    };

    if (error.response) {
      // 서버에서 응답을 받은 경우
      const { data, status } = error.response;
      errorResponse.message = data?.message || getDefaultErrorMessage(status);
      errorResponse.error_code = data?.error_code || `HTTP_${status}`;
      errorResponse.status = status;
    } else if (error.request) {
      // 요청은 보냈지만 응답을 받지 못한 경우
      errorResponse.message = '서버에 연결할 수 없습니다';
      errorResponse.error_code = 'CONNECTION_ERROR';
    }

    console.error('[API Error]', errorResponse);
    return Promise.reject(errorResponse);
  }
);

// 상태 코드별 기본 에러 메시지
function getDefaultErrorMessage(status) {
  const messages = {
    400: '잘못된 요청입니다',
    401: '인증이 필요합니다',
    403: '접근 권한이 없습니다',
    404: '요청한 정보를 찾을 수 없습니다',
    409: '이미 처리된 요청입니다',
    422: '입력값을 확인해주세요',
    429: '요청이 너무 많습니다. 잠시 후 다시 시도해주세요',
    500: '서버 오류가 발생했습니다',
    502: '서버가 응답하지 않습니다',
    503: '서비스를 일시적으로 이용할 수 없습니다',
  };
  return messages[status] || '알 수 없는 오류가 발생했습니다';
}

// ============================================
// 회원 API
// ============================================

/**
 * 전화번호로 회원 조회
 * @param {string} phone - 전화번호 (11자리)
 * @param {boolean} includeQrImage - QR 이미지 포함 여부
 */
export const getMemberByPhone = async (phone, includeQrImage = false) => {
  return api.get(`/member/by-phone/${phone}`, {
    params: { include_qr_image: includeQrImage },
  });
};

/**
 * QR 코드로 회원 조회
 * @param {string} qrCode - QR 코드 값
 * @param {boolean} includeQrImage - QR 이미지 포함 여부
 */
export const getMemberByQR = async (qrCode, includeQrImage = false) => {
  return api.get(`/member/by-qr/${encodeURIComponent(qrCode)}`, {
    params: { include_qr_image: includeQrImage },
  });
};

/**
 * 방문 이력 조회
 * @param {string} memberId - 회원 ID
 * @param {number} page - 페이지 번호
 * @param {number} pageSize - 페이지 크기 (최대 100)
 */
export const getVisitHistory = async (memberId, page = 1, pageSize = 100) => {
  return api.get(`/visit/history/${memberId}`, {
    params: { page, page_size: pageSize },
  });
};

/**
 * 주화 잔액 조회
 * @param {string} memberId - 회원 ID
 */
export const getCoinBalance = async (memberId) => {
  return api.get(`/coin/${memberId}/balance`);
};

// ============================================
// 주화 관리 API
// ============================================

/**
 * 수동 주화 지급
 * @param {string} memberId - 회원 ID
 * @param {number} amount - 지급 수량
 * @param {string} reason - 지급 사유
 */
export const manualEarnCoins = async (memberId, amount, reason) => {
  return api.post('/admin/coin/manual-earn', {
    member_id: memberId,
    amount,
    reason,
  });
};

/**
 * 수동 주화 회수
 * @param {string} memberId - 회원 ID
 * @param {number} amount - 회수 수량
 * @param {string} reason - 회수 사유
 */
export const manualDeductCoins = async (memberId, amount, reason) => {
  return api.post('/admin/coin/manual-deduct', {
    member_id: memberId,
    amount,
    reason,
  });
};

// ============================================
// 주렁은행: 환전/저축/기부 (bank/services/api.js 미러)
// ============================================

export const exchangeCoins = async ({ memberId, amount, storeId, operatorId }) =>
  api.post('/coin/exchange', {
    member_id: memberId,
    amount,
    store_id: storeId,
    operator_id: operatorId,
  });

export const depositCoins = async ({ memberId, amount, storeId, operatorId }) =>
  api.post('/coin/deposit', {
    member_id: memberId,
    amount,
    store_id: storeId,
    operator_id: operatorId,
  });

export const donateCoins = async ({ memberId, amount, storeId, operatorId }) =>
  api.post('/coin/donate', {
    member_id: memberId,
    amount,
    store_id: storeId,
    operator_id: operatorId,
  });

export const getTransactionHistory = async (memberId, page = 1, pageSize = 50) =>
  api.get(`/coin/${memberId}/history`, {
    params: { page, page_size: pageSize },
  });

export const getActivePolicies = async () => api.get('/coin/policies/active');

// ============================================
// 주렁맨: 주화 즉석 지급 (STAFF 이상)
// ============================================

/**
 * 주렁맨 주화 즉석 지급 — `POST /api/v1/tools/coin/give`
 * (설계: `docs/2026-07-07-zoolungman-coin-grant-app-dev-steps.md` §S1·§S4)
 *
 * 🔴 **`amount` 는 반드시 숫자여야 합니다.** 서버 `GiveCoinRequest.amount` 가 Pydantic
 *    `strict=True`(`api-server/src/routers/tools_coin.py`)라 문자열 `"3"` 을 보내면 **422** 입니다.
 *    호출부가 `<input type="number">` 의 `e.target.value`(문자열)를 그대로 넘기는 사고를 막기 위해
 *    **여기서 `Number()` 로 한 번 더 변환**합니다(방어선 2중화 — 호출부 `validateGiveAmount` 가 1차).
 *
 * ℹ️ 회원 조회는 신규 함수를 만들지 않고 위의 **기존 `getMemberByQR`** 를 그대로 씁니다
 *    (dev-steps §설계판단③ DRY — 대소문자만 다른 `getMemberByQr` 추가는 오import 사고를 부름).
 *
 * @param {{memberId: string, amount: number}} params
 * @returns {Promise<{success:boolean, member_id:string, amount:number,
 *                    balance_after:number, transaction_id:number}>}
 */
export const giveCoin = async ({ memberId, amount }) =>
  api.post('/tools/coin/give', {
    member_id: memberId,
    amount: Number(amount),
  });

// ============================================
// 알림톡 API
// ============================================

/**
 * QR 알림톡 재발송
 * @param {string} memberId - 회원 ID
 */
export const resendQrAlimtalk = async (memberId) => {
  return api.post(`/member/${memberId}/resend-qr`);
};

// ============================================
// 가족 API
// ============================================

/**
 * 가족 구성원 조회 (부모 + 자녀)
 * @param {string} memberId - 회원 ID (부모 또는 자녀)
 * @param {boolean} includeQrImages - QR 이미지 포함 여부
 */
export const getFamilyMembers = async (memberId, includeQrImages = false) => {
  return api.get(`/family/${memberId}/family`, {
    params: { include_qr_images: includeQrImages },
  });
};

/**
 * 자녀 계정 추가
 * @param {string} parentId - 부모 회원 ID
 * @param {object} childData - { nickname, name, birthday }
 */
export const addChildMember = async (parentId, { nickname, name, birthday }) => {
  return api.post(`/family/${parentId}/child`, {
    nickname,
    name: name || null,
    birthday: birthday || null,
  });
};

/**
 * 자녀 삭제 (비활성화, 잔액은 부모에게 이전)
 * @param {string} parentId - 부모 회원 ID
 * @param {string} childId - 자녀 회원 ID
 */
export const removeChildMember = async (parentId, childId) => {
  return api.delete(`/family/${parentId}/child/${childId}`);
};

/**
 * 회원 정보 수정 (닉네임, 이름, 생일)
 * @param {string} memberId - 회원 ID
 * @param {object} data - { nickname, name, birthday }
 */
export const updateMember = async (memberId, data) => {
  return api.patch(`/member/${memberId}`, data);
};

// ============================================
// 여권 API
// ============================================

/**
 * 회원의 전체 여권 목록 조회 (다중 여권 지원)
 * @param {string} memberId - 회원 ID
 * @param {boolean} includeExpired - 만료된 여권 포함 여부
 */
export const getAllMemberPassports = async (memberId, includeExpired = false) => {
  return api.get(`/passport/${memberId}/all`, {
    params: { include_expired: includeExpired },
  });
};

/**
 * 회원이 관여된 여권 할당 이력 조회
 * @param {string} memberId
 */
export const getPassportAssignmentLogs = async (memberId) => {
  return api.get(`/passport/${memberId}/assignment-logs`);
};

/**
 * 회원 여권 정보 조회
 * @param {string} memberId - 회원 ID
 */
export const getMemberPassport = async (memberId) => {
  return api.get(`/passport/${memberId}`);
};

/**
 * 여권 등록 (현장 발급)
 * @param {object} params - 등록 파라미터
 * @param {string} params.memberId - 회원 ID
 * @param {string} params.storeId - 발급 지점 ID
 * @param {string} params.issueDate - 발급일 (YYYY-MM-DD, 선택)
 */
export const registerPassport = async ({ memberId, storeId, issueDate }) => {
  return api.post('/passport/register', {
    member_id: memberId,
    store_id: storeId,
    issue_date: issueDate || undefined,
  });
};

// 여권 할당(이전) - 부모 여권을 자녀에게 할당
export const assignPassport = async (passportId, targetMemberId) => {
  return api.post(`/passport/${passportId}/assign`, {
    target_member_id: targetMemberId,
  });
};

// 여권 취소 — 여권·미사용 쿠폰·구매 주화·스탬프를 함께 회수합니다(삭제가 아닙니다).
export const cancelPassport = async (passportId) => {
  return api.delete(`/passport/${passportId}/cancel`);
};

// ============================================
// 여권취소(QR 스캔) — tools 전용, STAFF 이상 (S17 / 기획서 §4.12)
// ============================================

/**
 * 여권 QR 조회 — `GET /api/v1/tools/passport/by-qr/{code}`
 *
 * 🔴 응답의 `cancelable` / `blocked_reason` 이 **서버 결론**입니다. 화면은 그대로 읽기만 하고
 *    QR·여권 상태 조합으로 가능여부를 다시 추론하지 마십시오 — 서버 재판정과 어긋나면
 *    "버튼은 눌리는데 취소에서만 실패" 가 됩니다.
 *
 * @param {string} code 여권 코드(`ZLP_...`). URL 이 아니라 **코드 원문**을 넘깁니다
 *   (경로 파라미터라 슬래시가 들어가면 라우팅이 깨집니다 — 추출은 `extractPassportQRCode`).
 * @returns {Promise<{code:string, qr:object, passport:object|null, coupons:Array,
 *   coupon_summary:object, cancel_preview:object, cancelable:boolean, blocked_reason:string|null}>}
 */
export const getPassportByQr = async (code) =>
  api.get(`/tools/passport/by-qr/${encodeURIComponent(code)}`);

/**
 * 여권 QR 취소 — `POST /api/v1/tools/passport/by-qr/{code}/cancel`
 *
 * 🔴 `expectedPassportId` 는 **조회 때 본 대상**입니다. 스캔과 클릭 사이에 다른 운영자가
 *    처리했으면 서버가 409 로 끊습니다. 비워 보내면 그 방어가 사라지므로 항상 넘기십시오.
 *
 * @param {string} code 여권 코드
 * @param {{expectedPassportId?: string, reason?: string}} options 취소 사유는 선택(최대 500자)
 * @returns {Promise<{passport_id:string, recalled_coupons:number, preserved_coupons:number,
 *   reversed_coins:number, recalled_stamps:number, qr_status:string}>}
 */
export const cancelPassportByQr = async (code, { expectedPassportId, reason } = {}) => {
  const body = {};
  // 🔴 `undefined` 를 그대로 실으면 JSON 직렬화 단계에서 조용히 사라져
  //    "보냈는데 서버가 못 봤다" 를 디버깅하기 어려워집니다. 값이 있을 때만 담습니다.
  if (expectedPassportId) body.expected_passport_id = expectedPassportId;
  const trimmed = (reason || '').trim();
  if (trimmed) body.reason = trimmed;
  return api.post(`/tools/passport/by-qr/${encodeURIComponent(code)}/cancel`, body);
};

/**
 * 회원 등록만 처리 (방문 기록 없이 멤버십 가입만)
 * @param {string} phone - 전화번호 (11자리)
 * @param {string} storeId - 매장 ID
 */
export const registerMemberOnly = async (phone, storeId) => {
  return api.post('/visit/manual-welcome', {
    phone,
    store_id: storeId,
    registration_only: true,
  });
};

/**
 * 여권 혜택 조회
 * @param {string} memberId - 회원 ID
 */
export const getPassportBenefits = async (memberId) => {
  return api.get(`/passport/${memberId}/benefits`);
};

// ============================================
// 이벤트 API
// ============================================

/**
 * 활성 이벤트 목록 조회
 * @param {string} storeId - 매장 ID (선택)
 */
export const getActiveEvents = async (storeId) => {
  return api.get('/event/active', {
    params: storeId ? { store_id: storeId } : {},
  });
};

/**
 * 이벤트 생성
 */
export const createEvent = async ({ eventName, description, coinAmount, startDate, endDate, storeId, maxParticipants }) => {
  return api.post('/event', {
    event_name: eventName,
    description: description || undefined,
    coin_amount: coinAmount,
    start_date: startDate,
    end_date: endDate,
    store_id: storeId || undefined,
    max_participants: maxParticipants || undefined,
  });
};

/**
 * 이벤트 참여 + 주화 보상
 * @param {string} eventId - 이벤트 ID
 * @param {string} memberId - 회원 ID
 */
export const participateEvent = async (eventId, memberId) => {
  return api.post(`/event/${eventId}/participate`, {
    member_id: memberId,
  });
};

/**
 * 회원의 이벤트 참여 여부 확인
 */
export const checkEventParticipation = async (eventId, memberId) => {
  return api.get(`/event/${eventId}/check/${memberId}`);
};

/**
 * 이벤트 종료
 */
export const endEvent = async (eventId) => {
  return api.post(`/event/${eventId}/end`);
};

// ============================================
// USB 브릿지 앱 연동
//
// 키오스크(prod 모드)는 https://zl-tools.nxithub.com 에서 서빙되어 http://localhost:8080
// 호출 시 active mixed content 로 분류되어 Chromium 이 fetch 를 silently 차단한다.
// (setMixedContentMode=ALWAYS_ALLOW 가 page resource 에는 동작하지만 fetch/XHR 에는
// 일관되지 않음.)
//
// 해결: 키오스크 앱(KioskActivity) 의 WebView 에 ZlzlBridge JavascriptInterface 를
// 노출. 이 핸들이 있으면 native 가 직접 http://127.0.0.1:8080 을 호출 후 결과를
// JS 콜백으로 돌려준다 → mixed content 정책 자체를 통과하지 않음.
//
// 폴백: 브라우저(개발모드, 일반 PC) 에서는 ZlzlBridge 가 없으므로 fetch 사용.
const BRIDGE_URL = 'http://localhost:8080';
const BRIDGE_TIMEOUT_MS = 60000;

const __zlzlBridgePending = new Map();
let __zlzlBridgeReqSeq = 0;

if (typeof window !== 'undefined') {
  window.__zlzlBridgeResolve = (id, jsonStr) => {
    const pending = __zlzlBridgePending.get(id);
    if (!pending) return;
    __zlzlBridgePending.delete(id);
    clearTimeout(pending.timeoutId);
    try { pending.resolve(JSON.parse(jsonStr)); }
    catch (e) { pending.resolve({ ok: false, status: 0, error: 'parse: ' + (e && e.message) }); }
  };
}

const hasNativeBridge = () =>
  typeof window !== 'undefined' &&
  window.ZlzlBridge &&
  typeof window.ZlzlBridge.request === 'function';

const callNativeBridge = (method, path, bodyObj, timeoutMs = BRIDGE_TIMEOUT_MS) =>
  new Promise((resolve) => {
    const id = ++__zlzlBridgeReqSeq;
    const timeoutId = setTimeout(() => {
      if (__zlzlBridgePending.delete(id)) {
        resolve({ ok: false, status: 0, error: 'timeout' });
      }
    }, timeoutMs);
    __zlzlBridgePending.set(id, { resolve, timeoutId });
    try {
      window.ZlzlBridge.request(id, method, path, bodyObj == null ? null : JSON.stringify(bodyObj));
    } catch (e) {
      __zlzlBridgePending.delete(id);
      clearTimeout(timeoutId);
      resolve({ ok: false, status: 0, error: 'invoke: ' + (e && e.message) });
    }
  });

// 모든 브릿지 호출의 단일 진입점. native 우선, 없으면 fetch 폴백.
// 반환: { ok, status, json } 또는 { ok:false, status:0, error }
const callBridge = async (method, path, body = null, timeoutMs = BRIDGE_TIMEOUT_MS) => {
  if (hasNativeBridge()) {
    const r = await callNativeBridge(method, path, body, timeoutMs);
    if (!r.ok && r.error) return { ok: false, status: r.status || 0, error: r.error };
    let json = null;
    try { json = r.text ? JSON.parse(r.text) : null; } catch (_) { /* non-JSON */ }
    return { ok: r.ok, status: r.status, json };
  }
  try {
    const res = await fetch(`${BRIDGE_URL}${path}`, {
      method,
      headers: body == null ? undefined : { 'Content-Type': 'application/json' },
      body: body == null ? undefined : JSON.stringify(body),
      signal: AbortSignal.timeout(timeoutMs),
    });
    let json = null;
    try { json = await res.json(); } catch (_) { /* ignore */ }
    return { ok: res.ok, status: res.status, json };
  } catch (e) {
    return { ok: false, status: 0, error: e && e.message };
  }
};

// USB 프린터 연결 상태 확인 (브릿지 앱 → USB 프린터)
export const checkUsbBridge = async () => {
  const r = await callBridge('GET', '/status', null, 2500);
  // r.ok=false 또는 json 없음 = 브릿지 앱(native/HTTP 서버) 자체에 도달 실패.
  // r.json 존재 = 브릿지 도달 성공이며, json.connected 는 "프린터 USB 연결 여부"(별개 상태).
  // → reachable(브릿지 생존)과 connected(프린터 연결)를 분리해 호출처가 혼동하지 않도록 한다.
  // connected 필드는 기존 호출처 하위호환을 위해 그대로 보존한다.
  if (!r.ok || !r.json) return { reachable: false, connected: false };
  return { reachable: true, ...r.json };
};

// 브릿지 앱 업데이트 확인
export const checkBridgeUpdate = async () => {
  const r = await callBridge('GET', '/check-update', null, 5000);
  return r.ok ? r.json : null;
};

// 브릿지 앱 OTA 업데이트 트리거
export const triggerBridgeUpdate = async () => {
  const r = await callBridge('POST', '/update', null, 5000);
  if (!r.ok) return { success: false, message: '브릿지 앱에 연결할 수 없습니다' };
  return r.json || { success: true };
};

// USB 브릿지 앱으로 raster 전송 (실패해도 throw 안 함, 단계별 오류 반환)
const sendToUsbBridge = async (rasterBase64) => {
  const r = await callBridge('POST', '/print', { raster_base64: rasterBase64 }, 60000);
  if (r.error || r.status === 0) {
    return { success: false, phase: 'bridge_unreachable', message: '브릿지 앱에 연결할 수 없습니다 (localhost:8080)' };
  }
  if (!r.ok) {
    return { success: false, phase: 'bridge_error', message: (r.json && r.json.message) || 'USB 출력 실패' };
  }
  if (!r.json || !r.json.success) {
    return { success: false, phase: 'usb_send_failed', message: r.json && r.json.message };
  }
  return r.json;
};

// QR 스티커 프린터 API
// ============================================

/**
 * QR 스티커 출력
 * @param {string} qrCode - 스캔된 QR 코드 문자열
 * @param {boolean} printFamily - true면 가족 전원, false면 1장만
 * @param {number|null} printerId - 프린터 ID (미지정 시 지점 기본)
 */
export const printQrSticker = async (qrCode, printFamily = false, printerId = null) => {
  // USB bridge 사전 확인 — 미연결 시 API 호출 없이 즉시 반환
  const bridge = await checkUsbBridge();
  if (!bridge.connected) {
    return { success: false, no_printer: true, message: 'USB 프린터가 연결되어 있지 않습니다' };
  }

  const result = await api.post('/tools/print-qr', {
    qr_code: qrCode,
    print_family: printFamily,
    printer_id: printerId,
  });
  if (result.raster_base64) {
    const usbResult = await sendToUsbBridge(result.raster_base64);
    if (!usbResult.success) {
      reportUsbPrintError({
        error_message: usbResult.message,
        error_phase: usbResult.phase,
        printer_id: printerId,
        member_id: result.member_id,
        printed_count: result.printed_count,
        bridge_status: bridge,
      });
      return { ...result, success: false, usb_failed: true, message: usbResult.message || 'USB 프린터 출력 실패' };
    }
  }
  return result;
};

/**
 * QR 스티커 배치 출력 (선택한 회원들)
 * @param {string[]} memberIds - 출력할 회원 ID 목록
 * @param {number|null} printerId - 프린터 ID
 */
export const printQrBatch = async (memberIds, printerId = null) => {
  // USB bridge 사전 확인
  const bridge = await checkUsbBridge();
  if (!bridge.connected) {
    return { success: false, no_printer: true, message: 'USB 프린터가 연결되어 있지 않습니다' };
  }

  const result = await api.post('/tools/print-qr-batch', {
    member_ids: memberIds,
    printer_id: printerId,
  });
  if (result.raster_base64) {
    const usbResult = await sendToUsbBridge(result.raster_base64);
    if (!usbResult.success) {
      reportUsbPrintError({
        error_message: usbResult.message,
        error_phase: usbResult.phase,
        printer_id: printerId,
        printed_count: result.printed_count,
        bridge_status: bridge,
      });
    }
  }
  return result;
};

/**
 * 밴드QR 즉석 출력 (테스트 서버 전용)
 * 서명 없는 랜덤 밴드코드(ZLB_{12}_{16})를 count 장 생성해 프린터로 출력한다.
 * DB(qr_bands)에는 기록하지 않는다 — 등록은 고객이 앱으로 스캔할 때 TOFU.
 * @param {number} count - 출력 매수 (1~50)
 * @param {number|null} printerId - 프린터 ID
 */
export const printBandQr = async (count, printerId = null) => {
  // USB bridge 사전 확인 — 미연결 시 API 호출 없이 즉시 반환
  const bridge = await checkUsbBridge();
  if (!bridge.connected) {
    return { success: false, no_printer: true, message: 'USB 프린터가 연결되어 있지 않습니다' };
  }

  const result = await api.post('/tools/print-band-qr', {
    count,
    printer_id: printerId,
  });
  if (result.raster_base64) {
    const usbResult = await sendToUsbBridge(result.raster_base64);
    if (!usbResult.success) {
      reportUsbPrintError({
        error_message: usbResult.message,
        error_phase: usbResult.phase,
        printer_id: printerId,
        printed_count: result.printed_count,
        bridge_status: bridge,
      });
      return { ...result, success: false, usb_failed: true, message: usbResult.message || 'USB 프린터 출력 실패' };
    }
  }
  return result;
};

/**
 * 여권테스트 배치 인쇄 (테스트 서버 전용)
 * 발행된 배치의 코드 전량을 라벨로 출력한다. 인쇄 대상 코드는 **서버가 배치에서 조회**하며
 * 화면은 `batch_id` 만 넘긴다 — 코드 문자열을 실어 보내면 원장에 없는 값이 인쇄될 수 있다
 * (기획 §4.3 P-4).
 *
 * 🔴 인쇄 전 호스트 확인(`webapp_base` 에 localhost 포함 여부)은 **화면의 책임**이다(기획 E9).
 *    그 값은 `POST /issue`·`GET /codes` 응답에만 있고 이 함수는 받지 않으므로 여기서 판정하지 않는다.
 *
 * 절차는 `printBandQr` 과 동일하다 — 브릿지 사전확인 → API → raster 전송 → 실패 보고.
 *
 * 설계: docs/2026-08-28-tools-passport-test-design.md §4.3 / dev-steps S6
 * @param {number} batchId - 인쇄할 배치 ID
 * @param {number|null} printerId - 프린터 ID (미지정 시 지점 기본)
 */
export const printPassportTestBatch = async (batchId, printerId = null) => {
  // USB bridge 사전 확인 — 미연결 시 API 호출 없이 즉시 반환
  const bridge = await checkUsbBridge();
  if (!bridge.connected) {
    return { success: false, no_printer: true, message: 'USB 프린터가 연결되어 있지 않습니다' };
  }

  const result = await api.post('/tools/passport-test/print', {
    batch_id: batchId,
    printer_id: printerId,
  });
  if (result.raster_base64) {
    const usbResult = await sendToUsbBridge(result.raster_base64);
    if (!usbResult.success) {
      // 출력 대상이 회원이 아니라 배치라 `member_id` 는 보내지 않는다(서버 모델에서 optional).
      reportUsbPrintError({
        error_message: usbResult.message,
        error_phase: usbResult.phase,
        printer_id: printerId,
        printed_count: result.printed_count,
        bridge_status: bridge,
      });
      return { ...result, success: false, usb_failed: true, message: usbResult.message || 'USB 프린터 출력 실패' };
    }
  }
  return result;
};

/**
 * 지점 프린터 목록 조회
 */
export const getStorePrinters = async () => {
  return api.get('/tools/printers');
};

// USB 프린터 출력 오류를 서버에 보고 (텔레그램 알림)
export const reportUsbPrintError = async (params) => {
  try {
    await api.post('/tools/usb-print-error', {
      ...params,
      service_type: 'tools',
    });
  } catch { /* 보고 실패는 무시 */ }
};

/**
 * 프린터 상태 확인
 * @param {number|null} printerId - 프린터 ID
 */
export const getPrinterStatus = async (printerId = null) => {
  const params = printerId ? { printer_id: printerId } : {};
  return api.get('/tools/printer-status', { params });
};

// ============================================
// 지점 API
// ============================================

/**
 * 지점 목록 조회 (로그인 후 지점 선택용)
 */
// 티켓으로 회원 조회
export const getMemberByTicket = async (ticketId) => {
  return api.get(`/tools/ticket-member/${encodeURIComponent(ticketId)}`);
};

// 주문번호로 회원 조회
export const getMemberByOrder = async (orderId) => {
  return api.get(`/tools/order-member/${encodeURIComponent(orderId)}`);
};

// 티켓에 전화번호 연결 (회원 생성/연결)
export const linkPhoneToTicket = async (ticketId, phone) => {
  return api.post('/tools/ticket-link-phone', {
    ticket_id: ticketId,
    phone,
  });
};

export const getStores = async () => {
  return api.get('/auth/stores');
};

// 프린터 용지 현황 조회
export const getPrinterPaperStatus = async () => {
  return api.get('/tools/printer-paper');
};

// 프린터 용지 교체 (카운터 리셋)
export const resetPrinterPaper = async (printerId, paperTotal = 200) => {
  return api.post('/tools/printer-paper/reset', {
    printer_id: printerId,
    paper_total: paperTotal,
  });
};

// 프린터 서비스 할당 변경
export const assignPrinterService = async (printerId, serviceType) => {
  return api.post('/tools/printer-service-assign', {
    printer_id: printerId,
    service_type: serviceType,
  });
};

// ============================================
// tools 수동 쿠폰 차감 (scan / redeem / cancel)
// docs/zlzl_coupon_tools_redeem_dev_steps.md §4
// ============================================

/** 쿠폰 QR 스캔 → 검증 + 회원 표시 + 가격 3종 (조회만, 사용 처리 안 함). */
export const scanCoupon = async (qrValue) => {
  return api.get('/tools/coupon/scan', { params: { qr_value: qrValue } });
};

/** 쿠폰 사용 처리 — 멱등 tools_request_id 필수. confirmedDiscountAmount 는 자동계산 불가(R4) 시만.
 *  selectedProductId — 다수 상품 쿠폰에서 직원이 선택한 실제 구매 상품(scan 의 product_choices 중 1). */
export const redeemCoupon = async ({
  couponIssueId, toolsRequestId, confirmedDiscountAmount = null, selectedProductId = null,
  allowExpired = false, reason = null,
}) => {
  const body = { coupon_issue_id: couponIssueId, tools_request_id: toolsRequestId };
  if (confirmedDiscountAmount !== null && confirmedDiscountAmount !== undefined) {
    body.confirmed_discount_amount = confirmedDiscountAmount;
  }
  if (selectedProductId !== null && selectedProductId !== undefined) {
    body.selected_product_id = selectedProductId;
  }
  // 만료예외 수동 차감 (D4) — allow_expired=true 시 사유 필수(서버 검증).
  if (allowExpired) { body.allow_expired = true; body.reason = reason; }
  return api.post('/tools/coupon/redeem', body);
};

// ============================================
// PC(alt-tab) 오퍼레이터 강제 정정 — E1~E4 (tools_coupon_ops)
// 기획서 2026-07-02-kinderlabs-coupon-force-cancel-pc-app §5.3
// ============================================

/** 즉석 발급 대상 회원 식별 — 전화로 가족 전원(쿠폰 유무 무관, ACTIVE). */
export const getMemberLookup = async (phone) =>
  api.get('/tools/coupon/member-lookup', { params: { phone } });

/** 현재 지점 발급가능 쿠폰 정의 목록(전지점 + 해당지점 전용). */
export const getIssuableCoupons = async () =>
  api.get('/tools/coupon/issuable');

/** 오퍼레이터 즉석 쿠폰 발급 — 값 지급이라 사유 필수. */
export const issueCoupon = async ({ couponDefId, memberId, reason }) =>
  api.post('/tools/coupon/issue', {
    coupon_def_id: couponDefId,
    member_id: memberId,
    reason,
  });

/** E4 — 전화번호로 회원(가족 포함) usable 쿠폰 그룹 조회. 응답에 전화번호 미포함. */
export const searchMemberCoupons = async (phone) =>
  api.get('/tools/coupon/member-coupons', { params: { phone } });

/** 오늘(KST) 이 지점의 쿠폰 사용 목록 — 현장 사용취소 진입점. 전화는 서버 마스킹(phone_masked). */
export const listTodayRedemptions = async () => {
  return api.get('/tools/coupon/redemptions/today');
};

/** 쿠폰 사용 취소 — POS 구매취소 시 직원이 호출. 만료 전이면 used→usable 복귀. */
export const cancelCouponRedemption = async ({ couponIssueId, toolsRequestId, reason = null }) => {
  return api.post('/tools/coupon/cancel', {
    coupon_issue_id: couponIssueId,
    tools_request_id: toolsRequestId,
    reason,
  });
};

// ============================================
// 가상 키오스크: 여권 리셋 / preview (TEST 서버 전용)
// docs/2026-06-13-virtual-kiosk-passport-purchase-dev-steps.md R3·R4a
// ============================================

/**
 * 여권 preview 조회 — 전화번호로 묶인 회원 전원 + 각자 여권/여권쿠폰 현황(R4a, G4).
 * @param {string} phone - 전화번호 (11자리)
 * @returns {Promise<object>} BE 계약(설계 §4.1.1): 회원 배열 또는 {members:[...]} 래핑.
 *   member: { member_id, member_type, name,
 *             passports:[{passport_id, status, store_id, issue_date}],
 *             passport_coupon_count }
 */
export const getTestPassportPreview = async (phone) => {
  return api.get(`/tools/test-passport/${phone}`);
};

/**
 * 여권 리셋 — 묶인 member_id 전원의 여권(status 무관 전부) + 여권쿠폰(issue_source='passport')
 * + 스탬프 삭제. 비여권 쿠폰(campaign/manual/quest)·회원·주화·방문은 보존(R3, G2/G3/G4).
 * @param {string} phone - 전화번호 (11자리)
 */
export const resetTestPassport = async (phone) => {
  return api.delete(`/tools/test-passport/${phone}`);
};

// ============================================
// 퀘스트 쿠폰(QR 스탬프 랠리) — 현장 태깅 / 광장 TV
// docs/2026-06-08-quest-coupon-design.md §5.2·§5.3 / dev_steps P5
// base: /tools/quest, PART_TIME 디바이스 토큰 인증(인터셉터 X-Device-Token).
// store_id 는 서버가 토큰에서 추출하므로 클라이언트는 보내지 않는다.
// ============================================

/**
 * 태블릿 셋업 — 이 매장의 active 세트 + active 스팟 목록 조회.
 * @returns {Promise<{success:boolean, active_set:{id:number,name:string,reward_coupon_name:string,reward_presentation?:{waiting_screen_image_url?:string|null,headquarters_maps?:Array<{floor:number,image_url:string,width:number,height:number}>}}|null, spots:Array<{id:number,name:string,description:string,sort_order:number,waiting_screen_media_id?:number|null,waiting_screen_url?:string|null}>}>}
 */
export const getQuestSetup = async () => {
  return api.get('/tools/quest/setup');
};

/**
 * 스팟 태깅 — 회원 QR + 선택한 spot_id 전송. 진행도(done/total)·already_tagged 반환.
 *
 * 미션 종류 확장(마이그124, 설계 §7.3): ox_quiz·funny_photo 는 미션 결과를 함께 전송한다.
 *   - extra.mission_version : setup 에서 받은 그 스팟의 mission_config_version 그대로 전달(버전 정합).
 *   - extra.mission_result  : ox_quiz={answers:[{qid,chosen}]}(is_correct 미전송 — 서버 재채점 D10)
 *                             / funny_photo={captured:true}(무저장 D4) / qr=미전달.
 * qr 미션의 기존 호출 `tagQuestSpot(qr, spotId)` 는 extra 없이도 동작(하위호환).
 *
 * @param {string} memberQr - 스캔된 회원 QR(JRG_…)
 * @param {number} spotId - 이 태블릿에 바인딩된 스팟 ID
 * @param {{mission_version?:number, mission_result?:object}} [extra] - 미션 결과(ox/funny). qr 은 생략.
 * @returns {Promise<{success:boolean, message:string, member_nickname:string, done?:number, total?:number, already_tagged?:boolean}>}
 */
export const tagQuestSpot = async (memberQr, spotId, extra = null) => {
  const body = {
    member_qr: memberQr,
    spot_id: spotId,
  };
  // null/undefined 만 머지(서버 TagReq 가 extra="forbid" 이므로 알 수 없는 키는 넣지 않는다).
  if (extra) {
    if (extra.mission_version != null) body.mission_version = extra.mission_version;
    if (extra.mission_result != null) body.mission_result = extra.mission_result;
  }
  return api.post('/tools/quest/tag', body);
};

/**
 * 첫 미션 **재태그** 시 띄울 미션 상태 지도 — 읽기전용(서버가 run 을 만들지 않는다).
 *
 * 🔴 GET 이 아니라 POST 다 — 회원 QR 을 쿼리스트링에 실으면 접근로그에 그대로 남는다.
 * 기획: docs/2026-08-05-quest-first-mission-status-map-dev-steps.md §5 S1
 *
 * @param {string} memberQr - 스캔된 회원 QR(JRG_…)
 * @returns {Promise<{success:boolean, floor:number|null,
 *   map:{image_url:string,width:number,height:number}|null, done:number, total:number,
 *   spots:Array<{spot_id:number,name:string,done:boolean,x:number,y:number}>}>}
 */
export const fetchQuestMissionMap = async (memberQr) => {
  return api.post('/tools/quest/mission-map', { member_qr: memberQr });
};

/**
 * 재참여 사전조회(D9) — 읽기전용. ox_quiz/funny_photo 미션 실행 전에 호출해
 * 이미 참여한 스팟인지 확인(퀴즈·촬영 헛수고 방지). DB 기록 없음(서버 peek_tag_status, run 미생성).
 * @param {string} memberQr - 스캔된 회원 QR(JRG_…)
 * @param {number} spotId - 이 태블릿에 바인딩된 스팟 ID
 * @returns {Promise<{success:boolean, already_tagged:boolean, done:number, total:number}>}
 */
export const checkQuestSpot = async (memberQr, spotId) => {
  return api.post('/tools/quest/check', {
    member_qr: memberQr,
    spot_id: spotId,
  });
};

/**
 * TEST 서버 미션완료테스트 — QR 회원의 오늘 미션 완료상태를 읽는다.
 * QR은 접근로그에 남지 않도록 기존 퀘스트 API와 같이 POST 본문으로 전송한다.
 */
export const getQuestTestProgress = async (memberQr) => {
  return api.post('/tools/quest/test-progress/preview', { member_qr: memberQr });
};

/**
 * TEST 서버 미션완료테스트 — 체크된 spot id를 오늘의 완료 집합으로 저장한다.
 * 서버는 태그를 삭제하지 않고 soft reset하여 기존 보상 멱등 이력을 보존한다.
 */
export const saveQuestTestProgress = async (memberQr, questSetId, completedSpotIds) => {
  return api.post('/tools/quest/test-progress/save', {
    member_qr: memberQr,
    quest_set_id: questSetId,
    completed_spot_ids: completedSpotIds,
  });
};

/**
 * 첫 미션 인트로 영상 **재생 이탈 보고** (dev-steps S9 수신 / S13 발신).
 *
 * 아이가 영상을 못 본 채 진행된 것이므로 현장 장애다 → 서버가 구조화 로그 + 텔레그램으로
 * 운영팀에 알린다(CLAUDE.md §10 silent failure 금지).
 *
 * 🔴 **fire-and-forget** — 보고 실패가 재생 흐름을 막으면 안 된다. 여기서 예외를 흘리면
 *    이탈을 감지하고도 인사 단계로 못 넘어가 화면이 잠긴다. 선례: `reportUsbPrintError`.
 * 🔴 서버 `IntroVideoErrorReq` 는 `extra="forbid"` 다 — 계약에 없는 키(예: `service_type`)를
 *    넣으면 422 로 보고가 통째로 사라진다. 길이·범위도 서버 Field 제약에 맞춰 자른다
 *    (`reason` 1~40 / `video_url` ≤500 / `store_id` ≤20 / `spot_id` int≥1).
 *
 * `videoKind` 는 첫 미션 화면에서 연달아 재생되는 두 영상(인트로 / 퀘스트 동선 — 마이그168a)
 * 중 무엇이 실패했는지 알린다. 생략하면 서버가 'intro' 로 본다(구 단말 하위호환).
 *
 * @param {{reason:string, spotId?:number|null, videoUrl?:string|null, videoKind?:string}} params
 */
export const reportQuestIntroVideoError = async ({
  reason, spotId, videoUrl, videoKind,
} = {}) => {
  try {
    const body = { reason: String(reason || 'other').slice(0, 40) };
    const numericSpotId = Number(spotId);
    if (Number.isInteger(numericSpotId) && numericSpotId >= 1) body.spot_id = numericSpotId;
    if (videoUrl) body.video_url = String(videoUrl).slice(0, 500);
    if (videoKind) body.video_kind = String(videoKind).slice(0, 20);
    // store_id 는 서버에서 **참고용**이다(권위는 디바이스 토큰). 토큰과 다르면 알림에 병기된다.
    const storeId = localStorage.getItem('store_id');
    if (storeId) body.store_id = String(storeId).slice(0, 20);
    await api.post('/tools/quest/intro-video-error', body);
  } catch { /* 보고 실패는 무시 — 재생 흐름을 막지 않는다 */ }
};

/**
 * 광장 TV 상태 — 회원 QR 의 active 세트 완료/미완료 조회.
 * @param {string} memberQr - 스캔된 회원 QR(JRG_…)
 * @returns {Promise<{
 *   success:boolean,
 *   active_set:boolean,
 *   completed:boolean,
 *   coupon_name?:string,
 *   remaining?:number,
 *   animal_reward?:{
 *     animal_reward_status:'granted'|'already_granted'|'not_eligible',
 *     quest_run_id:number,
 *     member_animal_profile_id:number|null,
 *     animal_profile:{id:number, animal_id:number, name_ko:string, image_url:string}|null,
 *     reward_presentation:{video_url:string}|null
 *   }
 * }>}
 */
export const getQuestStatus = async (memberQr) => {
  return api.post('/tools/quest/status', {
    member_qr: memberQr,
  });
};

/**
 * 미션수동확인(마이그146) — 매장 active manual_check 스팟 목록.
 * @returns {Promise<{success:boolean, active_set:object|null, spots:Array}>}
 */
export const getManualCheckSpots = async () => {
  return api.get('/tools/quest/manual-check/spots');
};

/**
 * 미션수동확인 완료 — 오퍼레이터가 미션 확인 후 선택 spot + 고객 QR → 태깅+정산.
 * @param {string} memberQr - 스캔된 고객 QR(JRG_…/밴드)
 * @param {number} spotId - 선택한 manual_check 스팟 id
 * @returns {Promise<{success:boolean, message:string, done:number, total:number, already_tagged:boolean, spot_reward:object|null}>}
 */
export const completeManualCheck = async (memberQr, spotId) => {
  return api.post('/tools/quest/manual-check/complete', {
    member_qr: memberQr,
    spot_id: spotId,
  });
};

// 장치관리자 하트비트 전송 — 인터셉터가 X-Device-Token/X-Store-Id 자동 첨부, base=/api/v1
// (설계 2026-06-18-device-manager §6.1 / dev-steps S9)
export const sendDeviceHeartbeat = async (payload) => {
  return api.post('/device-mgr/heartbeat', payload);
};

export default api;
